import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useWishlist } from '@/hooks/useWishlist';
import { useCollection } from '@/hooks/useCollection';
import type { TradeListing } from '@/hooks/useCommunity';

export type TradeMatch = {
  listing: TradeListing & { match_score: number; match_reasons: string[] };
};

/**
 * Auto-match wishlist <-> trade listings.
 * A listing matches if:
 *  - it has a card the current user wants (wishlist)  +1
 *  - the user owns a card whose name appears in want_description  +1
 */
export function useTradeMatches(listings: TradeListing[] | undefined) {
  const { user } = useAuth();
  const { data: wishlist } = useWishlist();
  const { data: collection } = useCollection();

  return useQuery({
    queryKey: ['trade-matches', user?.id, listings?.length, wishlist?.length, collection?.length],
    enabled: !!user && !!listings,
    queryFn: () => {
      if (!user || !listings) return [] as TradeMatch[];
      const wishCardIds = new Set((wishlist || []).map(w => w.card_id));
      const ownedNames = new Set(
        (collection || [])
          .map(c => c.card?.name?.toLowerCase())
          .filter(Boolean) as string[],
      );

      const out: TradeMatch[] = [];
      for (const l of listings) {
        if (l.user_id === user.id) continue;
        let score = 0;
        const reasons: string[] = [];
        if (wishCardIds.has(l.have_card_id)) {
          score += 2;
          reasons.push('They have a card on your wishlist');
        }
        const want = (l.want_description || '').toLowerCase();
        for (const name of ownedNames) {
          if (name.length > 3 && want.includes(name)) {
            score += 1;
            reasons.push(`You own ${name}`);
            break;
          }
        }
        if (score > 0) {
          out.push({ listing: { ...l, match_score: score, match_reasons: reasons } });
        }
      }
      out.sort((a, b) => b.listing.match_score - a.listing.match_score);
      return out;
    },
  });
}

/** Quick lookup: does THIS listing match the current user? */
export function useIsMatch(listing: TradeListing) {
  const { user } = useAuth();
  const { data: wishlist } = useWishlist();
  const { data: collection } = useCollection();
  if (!user || listing.user_id === user.id) return null;
  const wishCardIds = new Set((wishlist || []).map(w => w.card_id));
  const reasons: string[] = [];
  if (wishCardIds.has(listing.have_card_id)) reasons.push('On your wishlist');
  const want = (listing.want_description || '').toLowerCase();
  const owned = (collection || []).find(c => {
    const n = c.card?.name?.toLowerCase();
    return n && n.length > 3 && want.includes(n);
  });
  if (owned) reasons.push(`You own ${owned.card?.name}`);
  return reasons.length ? reasons : null;
}

/** Lookup the supabase user-id behind a username (used to fetch profile-based realtime helpers) */
export async function fetchUserIdByUsername(username: string): Promise<string | null> {
  const { data } = await supabase.from('profiles').select('user_id').eq('username', username).maybeSingle();
  return data?.user_id ?? null;
}
